import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import UsersService from "../../services/UsersService";
import RolesService from "../../services/RolesService";
import EntitiesService from "../../services/EntitiesService";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import IUsersData from "../../types/Users";
import IRolesData from "../../types/Users";
import IEntityData from "../../types/Entities";
import { useDefaultStyles } from "../../common/styles/default";
import Banner from "../../common/components/banner";
import banner from "../../assets/images/banners/event_form_banner.png";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Grid from "@mui/material/Grid";

import {
  student,
  admin,
  entity_support,
  teacher,
  external_measurer,
} from "../extra-fields";
import { IconButton } from "@material-ui/core";

export default function FormUser() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const styles = useDefaultStyles();

  const [roles, setRoles] = useState<IRolesData[]>([]);
  const [entities, setEntities] = useState<IEntityData[]>([]);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [profile, setProfile] = useState("");
  const [entity, setEntity] = useState("");
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [rg, setRg] = useState("");
  const [email, setEmail] = useState("");
  const [ra, setRa] = useState("");
  const [fields, setFields] = useState<any[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleProfileChange = (event: SelectChangeEvent) => {
    setProfile(event.target.value);
    if (event.target.value == "5") {
      setFields(student);
    }
  };

  const handleEntityChange = (event: SelectChangeEvent) => {
    setEntity(event.target.value);
  };

  const arrangeExtraFields = (data: any) => {
    let arrExtra: Array<any> = [];
    fields.map((f) => {
      arrExtra.push({
        label: f.name,
        value: data.get(f.name),
      });
    });

    return arrExtra;
  };

  const { id } = useParams();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);
    const data = new FormData(event.currentTarget);
    const arrExtra: Array<any> = arrangeExtraFields(data);

    // Validação aprimorada
    if (name.trim() === "") {
      setError(t("toast_msg_formuser_name"));
      toast(t("toast_msg_formuser_name"), { type: "error" });
      setLoading(false);
      return;
    }
    if (email.trim() === "" || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError(t("toast_msg_formuser_email"));
      toast(t("toast_msg_formuser_email"), { type: "error" });
      setLoading(false);
      return;
    }
    if (entity === "") {
      setError(t("toast_msg_formuser_entity"));
      toast(t("toast_msg_formuser_entity"), { type: "error" });
      setLoading(false);
      return;
    }
    if (profile === "") {
      setError(t("toast_msg_formuser_profile"));
      toast(t("toast_msg_formuser_profile"), { type: "error" });
      setLoading(false);
      return;
    }
    // Validação de senha
    if (!id) { // Cadastro: senha obrigatória
      if (password === "") {
        setError(t("toast_msg_formuser_pass"));
        toast(t("toast_msg_formuser_pass"), { type: "error" });
        setLoading(false);
        return;
      }
      if (password.length < 8) {
        setError(t("toast_msg_formuser_pass_len"));
        toast(t("toast_msg_formuser_pass_len"), { type: "error" });
        setLoading(false);
        return;
      }
    } else { // Edição: senha opcional
      if (password && password.length < 8) {
        setError(t("toast_msg_formuser_pass_len"));
        toast(t("toast_msg_formuser_pass_len"), { type: "error" });
        setLoading(false);
        return;
      }
    }
    // Confirmação de senha
    if (password !== passwordConfirmation) {
      setError(t("toast_msg_formuser_pass_confirm"));
      toast(t("toast_msg_formuser_pass_confirm"), { type: "error" });
      setLoading(false);
      return;
    }
    // Validação simples de CPF (apenas dígitos e tamanho)
    // Remove caracteres especiais do CPF antes de validar
    if(cpf != "" && cpf != null) {
      const cleanCpf = cpf.replace(/[^\d]/g, "");
      if (cpf && !/^\d{11}$/.test(cleanCpf)) {
        setError(t("toast_msg_formuser_cpf_invalid"));
        toast(t("toast_msg_formuser_cpf_invalid"), { type: "error" });
        setLoading(false);
        return;
      }
    }

    const dataUsers = {
      name: name,
      cpf: cpf,
      rg: rg,
      email: email,
      ra: ra,
      role: profile,
      entity_id: entity,
      extra_fields: JSON.stringify(arrExtra),
      // Só envia senha se preenchida ou se for cadastro
      ...(password && { password: password, password_confirmation: passwordConfirmation }),
    };
    let responseUser;
    try {
      if (!id) {
        responseUser = await UsersService.store(dataUsers);
      } else {
        responseUser = await UsersService.update(id, dataUsers);
      }
      toast(responseUser?.data?.Msg, { type: "success" });
      setLoading(false);
      return;
    } catch (e: any) {
      const status = e?.response?.status;
      const backendMsg =
        status === 422
          ? e?.response?.data?.message
          : status === 400
          ? e?.response?.data?.Msg
          : e?.response?.data?.Msg ?? e?.response?.data?.message;
      const message = backendMsg
        ? t(backendMsg)
        : e?.message || t("toast_msg_formuser_generic_error");
      setError(message);
      toast(message, { type: "error" });
      setLoading(false);
    }
  };

  const loadUser = async (id: any) => {
    setLoading(true);
    await UsersService.get(id)
      .then((response: any) => {
        if (response) {
          setName(response.data.name);
          setEmail(response.data.email);
          setCpf(response.data.cpf);
          setRa(response.data.ra);
          setRg(response.data.rg);
          setProfile(response.data.roles[0].id);
          setEntity(response.data.entity_id);
          setPassword("");
          setPasswordConfirmation("");
        }
      })
      .catch((e: Error) => {
        setError(e.message);
        toast(e.message, { type: "error" });
      })
      .finally(() => setLoading(false));
  };

  const loadRoles = async () => {
    setLoading(true);
    await RolesService.getAll()
      .then((response: any) => {
        if (response) {
          setRoles(response.data);
        }
      })
      .catch((e: Error) => {
        setError(e.message);
        toast(e.message, { type: "error" });
      })
      .finally(() => setLoading(false));
  };

  const loadEntities = async () => {
    setLoading(true);
    await EntitiesService.getAll()
      .then((response: any) => {
        if (response) {
          setEntities(response.data);
        }
      })
      .catch((e: Error) => {
        setError(e.message);
        toast(e.message, { type: "error" });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadEntities();
    loadRoles();
    if (id) {
      loadUser(id);
    }
  }, []);

  return (
    <Box>
      <Banner
        type="small"
        image={banner}
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
            <PersonAddOutlinedIcon sx={{ width: "3em", height: "3em" }} />
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              {!id
                ? t("title_page_form_register_user")
                : t("title_page_form_register_user_edit")}
            </Typography>
          </Box>
        }
      />
      <Box className={styles.container}>
        {/* Exibe loading visual durante requisições */}
        {loading && (
          <Typography sx={{ color: 'primary.main', mb: 2 }}>
            {t('loading')}
          </Typography>
        )}
        {/* Exibe mensagem de erro se houver */}
        {error && (
          <Typography sx={{ color: 'error.main', mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            {/* Linha 1: Nome | E-mail */}
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="name"
                name="name"
                label={t("input_label_name")}
                variant="outlined"
                value={name}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setName(event.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                id="email"
                name="email"
                label={t("input_label_email")}
                variant="outlined"
                value={email}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(event.target.value);
                }}
              />
            </Grid>
            {/* Linha 2: Entidade | Perfil */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="entities-label">
                  {t("input_label_entity")}
                </InputLabel>
                <Select
                  labelId="entities-label"
                  id="entities"
                  label={t("input_label_entity")}
                  value={entity}
                  onChange={handleEntityChange}
                >
                  {entities.map((row) => (
                    <MenuItem value={row.id} key={row.id}>
                      {row.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="roles-label">
                  {t("input_label_profile")}
                </InputLabel>
                <Select
                  labelId="roles-label"
                  label={t("input_label_profile")}
                  value={profile}
                  onChange={handleProfileChange}
                >
                  {roles.map((row) => (
                    <MenuItem value={row.id} key={row.id}>
                      {t(row.name)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Linha 3: CPF | RG */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="cpf"
                name="cpf"
                label={t("input_label_document1")}
                variant="outlined"
                value={cpf}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setCpf(event.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="rg"
                name="rg"
                label={t("input_label_document2")}
                variant="outlined"
                value={rg}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setRg(event.target.value);
                }}
              />
            </Grid>
            {/* Linha 4: RA | (campos extras, se houver) */}
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                id="ra"
                name="ra"
                label={t("input_label_registration")}
                variant="outlined"
                value={ra}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setRa(event.target.value);
                }}
              />
            </Grid>
            {profile == "5" && fields.map((field, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <TextField
                  fullWidth
                  id={field.name}
                  label={field.label}
                  type={field.type}
                  name={field.name}
                  defaultValue={field.value}
                />
              </Grid>
            ))}
            {/* Linha 5: Senha | Confirmação de Senha */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TextField
                  fullWidth
                  id="outlined-read-only-input"
                  label={t("title_label_password")}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setPassword(event.target.value);
                  }}
                />
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="password-confirmation"
                label={t("title_label_password_confirm")}
                type={showPassword ? "text" : "password"}
                value={passwordConfirmation}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPasswordConfirmation(event.target.value);
                }}
              />
            </Grid>
            {/* Linha 6: Botões */}
            <Grid item xs={12} sm={6}>
              <Button type="submit" variant="contained" size="large" sx={{ mr: 2 }}>
                {t("btn_save")}
              </Button>
              <Button
                variant="contained"
                size="large"
                color="secondary"
                onClick={() => {
                  navigate("/users/list/");
                }}
              >
                {t("btn_return")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
